import { useState, useRef, useCallback } from 'react';

export interface FileUploadState {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

interface UseSequentialFileUploadProps {
  buildPayload: (file: File, documentId: string | undefined) => Record<string, any>;
  onUploadFile: (payload: any) => Promise<any>;
  extractCreatedId?: (result: any) => string | undefined;
  onComplete?: () => Promise<void> | void;
}

export const useSequentialFileUpload = ({
  buildPayload,
  onUploadFile,
  extractCreatedId,
  onComplete
}: UseSequentialFileUploadProps) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const documentIdRef = useRef<string | undefined>(undefined);

  const uploadFile = async (fileState: FileUploadState): Promise<void> => {
    setFiles(prev => prev.map(f =>
      f.id === fileState.id ? { ...f, status: 'uploading' } : f
    ));
    setUpdateCount(prev => prev + 1);

    try {
      const payload = buildPayload(fileState.file, documentIdRef.current);
      const result = await onUploadFile(payload);

      if (result?.error) {
        throw new Error(result.error.message || 'Upload failed');
      }

      if (extractCreatedId) {
        const receivedId = extractCreatedId(result);
        if (!documentIdRef.current && receivedId) {
          documentIdRef.current = receivedId;
        }
      }

      setFiles(prev => prev.map(f =>
        f.id === fileState.id ? { ...f, status: 'success' } : f
      ));
      setUpdateCount(prev => prev + 1);
    } catch (error: any) {
      setFiles(prev => prev.map(f =>
        f.id === fileState.id
          ? { ...f, status: 'error', errorMessage: error.message || 'Upload failed' }
          : f
      ));
      setUpdateCount(prev => prev + 1);
    }
  };

  const startQueue = async (filesToProcess?: FileUploadState[]): Promise<void> => {
    const queueFiles = filesToProcess || files;
    setIsProcessing(true);

    const pendingFiles = queueFiles.filter(f => f.status === 'pending' || f.status === 'error');
    for (const fileState of pendingFiles) {
      await uploadFile(fileState);
    }

    if (onComplete) {
      await onComplete();
    }

    setIsProcessing(false);
  };

  const retryFile = async (fileId: string): Promise<void> => {
    const fileState = files.find(f => f.id === fileId);
    if (!fileState) return;
    await uploadFile(fileState);
  };

  const addFiles = useCallback((newFiles: File[]): FileUploadState[] => {
    const fileStates: FileUploadState[] = newFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const
    }));
    setFiles(prev => [...prev, ...fileStates]);
    setUpdateCount(prev => prev + 1);
    return fileStates;
  }, []);

  const clearSuccessfulFiles = useCallback(() => {
    setFiles(prev => prev.filter(f => f.status === 'error'));
    setUpdateCount(prev => prev + 1);
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    documentIdRef.current = undefined;
    setUpdateCount(prev => prev + 1);
  }, []);

  const hasErrors = files.some(f => f.status === 'error');
  const allSuccess = files.length > 0 && files.every(f => f.status === 'success');
  const hasPending = files.some(f => f.status === 'pending' || f.status === 'error');

  return {
    files,
    isProcessing,
    hasErrors,
    allSuccess,
    hasPending,
    startQueue,
    retryFile,
    addFiles,
    clearFiles,
    clearSuccessfulFiles,
    updateCount
  };
};
